<template lang="pug">
UiModal(
  :show="show"
  @close="close"
  :title="$t('companies.reports.create_title')"
  :rightActions="rightActions"
  width="620"
)
  UiFormGroup(
    :label="$t('companies.reports.report_type')"
    :vuelidateModel="v$.selectedReportType"
  )
    UiMultiselect(
      v-model="selectedReportType"
      :options="reportsOptions"
      :canDeselect="false"
      :canClear="false"
      :error="v$.selectedReportType.$error"
    )

  UiFormGroup(:label="$t('companies.reports.schedule_type')")
    UiStack(
      vertical
      v-if="showSchedule"
    )
      UiRadio(
        v-model="report.schedule"
        value="single"
      ) {{ $t("companies.reports.schedule_single") }}
      UiRadio(
        v-model="report.schedule"
        value="schedule"
      ) {{ $t("companies.reports.schedule_schedule") }}
      UiRadio(
        v-model="report.schedule"
        value="every_day"
        v-if="showEveryDaySchedule"
      ) {{ $t("companies.reports.schedule_every_day") }}
      UiRadio(
        v-model="report.schedule"
        value="for_every_day"
        v-if="showForEveryDaySchedule"
      ) {{ $t("companies.reports.schedule_for_every_day") }}
      UiRadio(
        v-model="report.schedule"
        :value="-1 * (new Date().getDay() + 1)"
        v-if="showForEveryWeekSchedule"
      ) {{ `${$t("companies.reports.schedule_for_every_week")} ${["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][new Date().getDay()]}` }}

      template(v-if="showHours")
        UiRadio(
          v-model="report.schedule"
          value="specific_time"
        ) {{ $t("companies.reports.scheduled_at_time") }}
        UiRadio(
          v-model="report.schedule"
          value="period"
        ) {{ $t("companies.reports.scheduled_by_period") }}

  UiFormGroup(
    :label="$t('companies.reports.schedule_time')"
    v-if="report.schedule === 'specific_time'"
  )
    UiTimepicker(v-model="report.daily_run_at")
  UiFormGroup(
    :label="$t('companies.reports.schedule_time_period')"
    v-if="report.schedule === 'period'"
  )
    UiMultiselect(
      v-model="report.run_period"
      :options="time_periods"
      :canDeselect="false"
      :canClear="false"
    )

  UiFormGroup(
    :label="$t('companies.reports.schedule_day')"
    v-if="report.schedule === 'schedule'"
  )
    UiInput(
      v-model="report.schedule_day"
      type="number"
      min="1"
      max="15"
    )

  UiFormGroup(v-if="showDates")
    .row
      .col-6
        UiFormGroup(label="Start date")
          UiDatePicker(
            v-model="report.date_start"
            canClear
          )
      .col-6
        UiFormGroup(label="End date")
          UiDatePicker(
            v-model="report.date_end"
            canClear
          )

  UiFormGroup(
    :label="$t('companies.reports.account_numbers_range')"
    v-if="showAccountRange"
  )
    .row
      .col-6
        UiFormGroup(label="From")
          UiInput(
            v-model="report.number_from"
            type="number"
          )
      .col-6
        UiFormGroup(label="To")
          UiInput(
            v-model="report.number_to"
            type="number"
          )

  UiFormGroup(
    :label="$t('companies.reports.default_bank_account')"
    v-if="showDefaultBankAccount"
  )
    UiInput(v-model="report.default_bank_account")

  UiFormGroup(v-if="google_drive_available && !!selectedReportType")
    UiCheckbox(
      v-model="report.google_drive"
      switch
    ) {{ $t("companies.reports.save_to_google_drive") }}

  UiFormGroup(
    :label="currentReport.file_upload_label"
    v-if="showUpload"
  )
    UiFilePicker(v-model="file")

  UiFormGroup(
    :label="currentReport.second_file_upload_label"
    v-if="showSecondUpload"
  )
    UiFilePicker(v-model="second_file")

  UiFormGroup(v-if="showDownloadSftp")
    UiCheckbox(
      v-model="download_from_sftp"
      switch
    ) {{ $t("companies.reports.download_from_sftp") }}

  UiFormGroup(
    :label="$t('companies.reports.download_sftps_items')"
    v-if="showDownloadSftp && download_from_sftp"
  )
    .form-container.mt-5(
      v-for="(item, index) in currentReport.sftp_download_keys"
      :key="item.key"
    )
      RequiredMultiselect(
        :label="item.label"
        v-model="report.sftp_download_keys[index].value"
        :options="sftp_storages"
      )

  UiFormGroup(v-if="showUploadSftp")
    UiCheckbox(
      v-model="upload_to_sftp"
      switch
    ) {{ $t("companies.reports.sftp_for_upload") }}

  UiFormGroup(
    :label="$t('companies.reports.upload_sftps_items')"
    v-if="showUploadSftp && upload_to_sftp"
  )
    .form-container.mt-5(
      v-for="(item, index) in currentReport.sftp_upload_keys"
      :key="item.key"
    )
      RequiredMultiselect(
        :label="item.label"
        v-model="report.sftp_upload_keys[index].value"
        :options="sftp_storages"
      )

  UiFormGroup(v-if="showPartnerChooser")
    UiCheckbox(
      v-model="report.new_partner_key"
      switch
    ) {{ $t("companies.reports.new_partner_key") }}

  UiFormGroup(
    v-if="showSourceAccount"
    :label="$t('companies.reports.source_account')"
    :vuelidateModel="v$.report.source_account"
  )
    UiInput(
      v-model="report.source_account"
      :error="v$.report.source_account.$error"
    )

  UiFormGroup(v-if="showFileFormat")
    a(
      :href="`/${currentReport.file_format}`"
      target="_blank"
    ) {{ currentReport.file_format_text }}
</template>

<script>
import ApiCompanyReports from "@/api/companyReports.js";
import ApiCompanyStorages from "@/api/companyStorages.js";
import { dateToDateString } from "@/common/date_to_date_string";
import { addMonths } from "@/common/date_utils";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import RequiredMultiselect from "./createReport/RequiredMultiselect.vue";

const today = new Date();
const nextMonth = addMonths(new Date(), 1);

export default {
  components: {
    RequiredMultiselect,
  },

  props: {
    iban: {
      type: String,
      defalt: null,
    },
  },

  setup() {
    return {
      v$: useVuelidate(),
    };
  },

  validations() {
    return {
      selectedReportType: {
        required,
      },
      report: {
        source_account: {
          required: requiredIf(this.showSourceAccount),
        },
      },
    };
  },

  data() {
    return {
      show: false,
      companyId: null,
      reportTypes: [],
      selectedReportType: null,
      report: {
        report_type: null,
        shedule: null,
        schedule_day: 1,
        date_start: today,
        date_end: nextMonth,
        number_from: null,
        number_to: null,
        sftp_download_keys: [],
        sftp_upload_keys: [],
        google_drive: false,
        new_partner_key: false,
        source_account: null,
      },
      file: null,
      second_file: null,
      upload_to_sftp: false,
      download_from_sftp: false,
      sftp_storages: [],
      time_periods: ["30m", "1h", "2h", "3h", "4h", "6h"].map((i) => ({
        label: i,
        value: i,
      })),
    };
  },

  watch: {
    selectedReportType: function (value) {
      this.changeReportType(value);
      this.report.sftp_upload_keys = this.currentReport.sftp_upload_keys;
      this.report.sftp_download_keys = this.currentReport.sftp_download_keys;
      this.report.new_partner_key = !!this.currentReport.new_partner_key;
    },
    download_from_sftp: function (value) {
      if (value) {
        this.loadSFTPStorages();
      }
    },
    upload_to_sftp: function (value) {
      if (value) {
        this.loadSFTPStorages();
      }
    },
  },

  computed: {
    rightActions() {
      return [
        {
          label: this.$t("actions.save"),
          icon: "Check",
          onClick: this.save,
        },
      ];
    },

    currentReport() {
      return (
        this.reportTypes.find((i) => i.name === this.selectedReportType) || {}
      );
    },

    google_drive_available() {
      return this.currentReport.google_drive_available;
    },

    showDefaultBankAccount() {
      return !!this.currentReport.default_bank_account;
    },

    showAccountRange() {
      return !!this.currentReport.with_accounts_range;
    },

    showDates() {
      return !!this.currentReport.with_dates;
    },

    showSchedule() {
      return !!this.currentReport.with_schedule;
    },

    showHours() {
      return !!this.currentReport.with_hours;
    },

    showEveryDaySchedule() {
      return !!this.currentReport.every_day;
    },

    showForEveryDaySchedule() {
      return !!this.currentReport.for_every_day;
    },

    showForEveryWeekSchedule() {
      return !!this.currentReport.for_every_week;
    },

    showUpload() {
      return (
        !!this.currentReport.upload &&
        !this.download_from_sftp &&
        !this.currentReport.sftp_only
      );
    },

    showSecondUpload() {
      return !!this.currentReport.double_upload && !this.download_from_sftp;
    },

    showFileFormat() {
      return !!this.currentReport.file_format;
    },

    showDownloadSftp() {
      this.download_from_sftp = null;
      return !!this.currentReport.sftp_download;
    },

    showUploadSftp() {
      this.upload_to_sftp = null;
      return !!this.currentReport.sftp_upload;
    },

    showPartnerChooser() {
      return this.currentReport.software === "netvisor";
    },

    showSourceAccount() {
      return this.currentReport.name === "netvisor_payments";
    },

    reportsOptions() {
      return this.reportTypes.map((i) => {
        return { label: i.label, value: i.name };
      });
    },
  },

  methods: {
    open(companyId, Item) {
      this.companyId = companyId;
      this.selectedReportType = null;
      this.report = {
        report_type: null,
        shedule: null,
        schedule_day: 1,
        date_start: today,
        date_end: nextMonth,
        number_from: null,
        number_to: null,
        sftp_download_keys: [],
        sftp_upload_keys: [],
        google_drive: false,
        new_partner_key: false,
      };
      this.file = null;
      this.second_file = null;
      this.upload_to_sftp = false;
      this.download_from_sftp = false;

      if (Item) {
        this.report.id = Item.id;
        this.report.report_type = Item.report_type;
        this.selectedReportType = Item.report_type;
      }
      this.file = undefined;
      this.second_file = undefined;
      ApiCompanyReports.fetchReportTypes(this.companyId).then((resp) => {
        this.reportTypes = resp.data.reports;
        this.show = true;
        this.v$.$reset();
      });
    },

    set_default_values() {
      if (this.showDefaultBankAccount) {
        this.report.default_bank_account = this.iban;
      }
    },

    changeReportType(reportType) {
      try {
        this.report.report_type = this.reportTypes.find(
          (i) => i.name == reportType,
        ).name;
        this.set_default_values();
      } catch {}
    },

    close() {
      this.show = false;
    },

    save() {
      this.v$.$validate().then((res) => {
        if (!res) return;

        // prettier-ignore
        let params = {
          report: {
            ...this.report,
            date_start: this.report.date_start && dateToDateString(this.report.date_start),
            date_end: this.report.date_end && dateToDateString(this.report.date_end)
          },
        };

        if (Boolean(this.file)) {
          const blobToBase64 = (blob) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            return new Promise((resolve) => {
              reader.onloadend = () => {
                resolve(reader.result);
              };
            });
          };
          blobToBase64(this.file).then((data) => {
            params.report.file = data;
            params.report.filename = this.file.name;
            if (Boolean(this.second_file)) {
              blobToBase64(this.second_file).then((data) => {
                params.report.second_file = data;
                params.report.second_filename = this.second_file.name;
                this.sendReport(this.companyId, params);
              });
            } else {
              this.sendReport(this.companyId, params);
            }
          });
        } else {
          this.sendReport(this.companyId, params);
        }
      });
    },

    sendReport(companyId, params) {
      if (params.report.id) {
        ApiCompanyReports.updateReport(companyId, params.report.id, params)
          .then((res) => {
            this.$emit("done");
            this.close();
          })
          .catch((e) => {});
      } else {
        ApiCompanyReports.createReport(companyId, params)
          .then((res) => {
            this.$emit("done");
            this.report.new_partner_key = false;
            this.close();
          })
          .catch((e) => {});
      }
    },

    loadSFTPStorages() {
      this.sftp_storages = []; // reset stprage list
      ApiCompanyStorages.fetchStorages(this.companyId).then((res) => {
        this.sftp_storages = res.data.sftp.map((x) => {
          return { text: x.name || x.host, value: x.id };
        });
      });
    },

    SFTPfolderUp() {
      if (this.report.sftp_folder == "./") {
        return;
      }
      this.report.sftp_folder = this.report.sftp_folder
        .split("/")
        .slice(0, -1)
        .join("/");
      if (this.report.sftp_folder === ".") {
        this.report.sftp_folder = "./";
      }
      ApiCompanyStorages.SFTPFolderList(
        this.$route.params.id,
        this.report.sftp_folder,
      ).then((resp) => {
        this.sftpFolders = resp.data.folders;
      });
    },

    changeSelectedSFTPFolder() {
      this.report.sftp_folder = this.report.sftp_folder || "./";
      let path =
        this.report.sftp_folder === "./"
          ? `${this.report.sftp_folder}${this.sftpSelectedFolder}`
          : `${this.report.sftp_folder}/${this.sftpSelectedFolder}`;
      this.loadFolder(path);
      this.report.sftp_folder = path;
    },

    loadRootFolder() {
      if (this.download_from_sftp && this.showDownloadSftp) {
        this.report.sftp_folder = this.report.sftp_folder || "./";
        this.loadFolder(this.report.sftp_folder);
      } else {
        this.report.sftp_folder = null;
      }
    },

    loadFolder(path) {
      ApiCompanyStorages.SFTPFolderList(this.$route.params.id, path, null).then(
        (resp) => {
          this.sftpFolders = resp.data.folders;
          this.report.sftp_folder = path;
        },
      );
    },
  },
};
</script>
